<template>
  <div
    class="ag-cell-text-renderer__text"
    :class="{
      'ag-cell-text-renderer__text--is-editable': isEditable,
    }"
    data-test-id="grid-list__text-renderer"
  >
    <span
      :class="{
        'ag-cell-text-renderer__wrapped-column': isColumnWrapped,
        'ag-cell-text-renderer__empty-value': !params.value && params.valueWhenEmpty,
      }"
      >{{ value }}
      <Btn
        v-if="isColumnWrapped && defaultLimit < params.value?.length"
        @click="toggleLimit"
        size="small"
        color="grey"
        >{{ $t(valueLimit === Infinity ? 'action.showLess' : 'action.showAll') }}</Btn
      >
    </span>

    <transition name="fade-transition">
      <span class="ag-cell__gradient" v-if="!isColumnWrapped && value !== ''"></span>
    </transition>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { ICellRendererParams } from 'ag-grid-community';
import { GridableEntity } from '../utils/types';

const defaultLimit = 1000;

export default defineComponent({
  name: 'TextRenderer',

  props: {
    params: {
      type: Object as PropType<
        ICellRendererParams<GridableEntity, string> & {
          isEditable: boolean;
          valueWhenEmpty?: string;
        }
      >,
      required: true,
    },
  },

  data(): {
    isColumnWrapped: boolean;
    colId: string;

    defaultLimit: number;
    valueLimit: number;
  } {
    return {
      isColumnWrapped: true,
      colId: '',
      defaultLimit,
      valueLimit: defaultLimit,
    };
  },

  computed: {
    isEditable(): boolean {
      return this.params.isEditable;
    },

    value(): string {
      if (!this.params.value && this.params.valueWhenEmpty) {
        return this.params.valueWhenEmpty;
      }

      const limited = this.params.value?.slice(0, this.valueLimit) ?? '';

      if (limited.length < this.params.value?.length) {
        return `${limited}…`;
      }

      return limited;
    },
  },

  created() {
    this.colId = this.params.column.getColId();
    this.$watch(
      () => this.$store.state.gridWrap.wrappedColumns[this.colId],
      () => {
        this.isColumnWrapped = this.$store.getters['gridWrap/isColumnWrapped'](this.colId);
      },
      { immediate: true }
    );
  },

  methods: {
    toggleLimit() {
      this.valueLimit = this.valueLimit === Infinity ? defaultLimit : Infinity;
    },
  },
});
</script>

<style lang="scss">
@use 'settings/colors';
@use 'mixins/typography';
@use 'mixins/base-renderer';
@use 'mixins/cell-gradient';

@include cell-gradient.cell-gradient;

.ag-cell-text-renderer__text {
  @include base-renderer.base-renderer;
  padding-top: 16px;
  padding-bottom: 16px;
  position: relative;
  overflow: hidden;
  @include typography.typography-14-normal(24px);
  user-select: text;

  &--is-editable {
    cursor: text;
  }
}

.ag-cell-wrap-text .ag-cell-text-renderer__text {
  word-break: break-word;
}

.ag-cell-text-renderer__wrapped-column {
  white-space: normal;
}

.ag-cell-text-renderer__empty-value {
  color: colors.$color-grey-400;
}
</style>
